/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Timeline component for displaying bike bus stops in chronological order.
Shows the journey progression with times and stop information.
*/

import { format } from "date-fns";
import { MapPin, Clock, UserPlus, UserMinus } from "lucide-react";
import { BikeBusStop, BikeBusMember } from "@/api/bikeBusService";

interface BikeBusTimelineProps {
  stops: BikeBusStop[];
  members?: BikeBusMember[];
  date: string;
}

export const BikeBusTimeline = ({ stops, members = [], date }: BikeBusTimelineProps) => {
  // Sort stops by time to ensure chronological order (timezone-safe)
  const sortedStops = [...stops].sort((a, b) => {
    // Extract time strings for comparison to avoid timezone conversion
    const getTimeString = (timeString: string) => {
      if (timeString.includes('T')) {
        return timeString.split('T')[1]; // Get time part from datetime
      }
      return timeString; // Already just time
    };
    
    const timeA = getTimeString(a.time);
    const timeB = getTimeString(b.time);
    
    return timeA.localeCompare(timeB); // String comparison works for HH:MM format
  });

  // Helper function to get users getting on/off at each stop
  const getUsersAtStop = (stopId: string, stopIndex: number) => {
    const gettingOn = members.filter(member => member.startStopId === stopId);
    const gettingOff = members.filter(member => member.endStopId === stopId);
    
    // For users without specific stops, assume they join at first stop and leave at last stop
    const isFirstStop = stopIndex === 0;
    const isLastStop = stopIndex === sortedStops.length - 1;
    const membersWithoutStops = members.filter(member => !member.startStopId && !member.endStopId);
    
    if (isFirstStop) {
      gettingOn.push(...membersWithoutStops);
    }
    if (isLastStop) {
      gettingOff.push(...membersWithoutStops);
    }
    

    
    return { gettingOn, gettingOff };
  };

  const formatStopTime = (timeString: string) => {
    try {
      // Convert stored UTC time back to London time for display
      if (timeString.includes('T')) {
        let utcDate: Date;
        
        // Handle different timestamp formats
        if (timeString.includes('+') || timeString.endsWith('Z')) {
          // Already has timezone info (e.g., "2025-07-31T07:00:00+00:00" or "2025-07-31T07:00:00Z")
          utcDate = new Date(timeString);
        } else {
          // No timezone info, assume UTC (e.g., "2025-07-31T07:00:00")
          const utcDateTime = `${timeString}Z`; // Add Z to indicate UTC
          utcDate = new Date(utcDateTime);
        }
        
        // Validate UTC date parsing
        if (isNaN(utcDate.getTime())) {
          return timeString; // Fallback to original
        }
        
        // Format as London time
        const londonTime = utcDate.toLocaleTimeString('en-GB', {
          timeZone: 'Europe/London',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        // Validate formatting result
        if (!londonTime || londonTime === 'Invalid Date') {
          return timeString; // Fallback to original
        }
        
        return londonTime;
      }
      // If it's already just time (HH:MM), assume it's already in London time
      return timeString;
    } catch (error) {
      // Fallback for any parsing errors
      return timeString;
    }
  };

  if (sortedStops.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No stops scheduled for this bike bus
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="font-medium flex items-center gap-2">
        <Clock className="h-4 w-4" />
        Journey Timeline
      </h4>
      
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
        
        {/* Timeline items */}
        <div className="space-y-6">
          {sortedStops.map((stop, index) => {
            const isFirst = index === 0;
            const isLast = index === sortedStops.length - 1;
            const { gettingOn, gettingOff } = getUsersAtStop(stop.id, index);
            
            return (
              <div key={`timeline-${stop.bikeBusId}-${stop.siteId}-${stop.index || index}`} className="relative flex items-start gap-4">
                {/* Timeline dot */}
                <div className={`relative z-10 flex h-3 w-3 items-center justify-center rounded-full border-2 ${
                  isFirst ? 'bg-green-500 border-green-500' : 
                  isLast ? 'bg-red-500 border-red-500' : 
                  'bg-blue-500 border-blue-500'
                }`}>
                  <div className="h-1.5 w-1.5 rounded-full bg-white"></div>
                </div>
                
                {/* Stop content */}
                <div className="flex-1 min-w-0 pb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        isFirst ? 'text-green-600' : 
                        isLast ? 'text-red-600' : 
                        'text-blue-600'
                      }`}>
                        Stop {stop.index || (index + 1)}
                      </span>
                      {isFirst && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Start</span>}
                      {isLast && <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">End</span>}
                    </div>
                    <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                      <Clock className="h-3 w-3" />
                      {formatStopTime(stop.time)}
                    </div>
                  </div>
                  
                  <div className="mt-1">
                    <p className="text-sm font-medium text-foreground">{stop.stop}</p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <MapPin className="h-3 w-3" />
                      {stop.siteName || 'Unknown Site'}
                    </div>
                  </div>

                  {/* User dots for getting on/off */}
                  {(gettingOn.length > 0 || gettingOff.length > 0) && (
                    <div className="mt-3 space-y-3">
                      {gettingOn.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 text-xs text-green-600 font-medium">
                            <UserPlus className="h-3 w-3" />
                            <span>Getting on:</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {gettingOn.map((member, memberIndex) => {
                              const initials = member.userName
                                .split(' ')
                                .map(n => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2);
                              
                              return (
                                <div
                                  key={`on-${member.userId}-${memberIndex}`}
                                  className="flex flex-col items-center gap-1"
                                >
                                  <div
                                    className="h-6 w-6 rounded-full bg-green-500 border-2 border-green-600 flex items-center justify-center text-white text-xs font-bold"
                                    title={`${member.userName} getting on`}
                                  >
                                    {initials}
                                  </div>
                                  <span className="text-xs text-muted-foreground text-center max-w-16 truncate">
                                    {member.userName.split(' ')[0]}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {gettingOff.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 text-xs text-red-600 font-medium">
                            <UserMinus className="h-3 w-3" />
                            <span>Getting off:</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {gettingOff.map((member, memberIndex) => {
                              const initials = member.userName
                                .split(' ')
                                .map(n => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2);
                              
                              return (
                                <div
                                  key={`off-${member.userId}-${memberIndex}`}
                                  className="flex flex-col items-center gap-1"
                                >
                                  <div
                                    className="h-6 w-6 rounded-full bg-red-500 border-2 border-red-600 flex items-center justify-center text-white text-xs font-bold"
                                    title={`${member.userName} getting off`}
                                  >
                                    {initials}
                                  </div>
                                  <span className="text-xs text-muted-foreground text-center max-w-16 truncate">
                                    {member.userName.split(' ')[0]}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Journey summary */}
      <div className="mt-6 p-3 bg-muted/50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium">Total Stops:</span>
            <span>{sortedStops.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">Total Members:</span>
            <span>{members.length}</span>
          </div>
          {sortedStops.length >= 2 && (
            <div className="flex items-center gap-2">
              <span className="font-medium">Duration:</span>
              <span>
                {formatStopTime(sortedStops[0].time)} - {formatStopTime(sortedStops[sortedStops.length - 1].time)}
              </span>
            </div>
          )}
        </div>
        
        {members.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
                <span>Getting on</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-red-500"></div>
                <span>Getting off</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 